import { Alert, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { parseMutationError } from '../api/errors';
import { useAssignProduct, useProducts, type ProductSummary } from '../api/products';

interface Props {
  opened: boolean;
  onClose: () => void;
  orgId: string;
  alreadyAssignedProductIds: string[];
}

export function AssignProductModal({ opened, onClose, orgId, alreadyAssignedProductIds }: Props) {
  const productsQuery = useProducts({ includeDeleted: false });
  const assign = useAssignProduct(orgId);

  const [productId, setProductId] = useState<string | null>(null);
  const [jwksUrl, setJwksUrl] = useState('');
  const [issuer, setIssuer] = useState('');
  const [audience, setAudience] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Filter to active, not-already-assigned products. Sort by code so the
  // dropdown is alphabetical.
  const candidates = useMemo<ProductSummary[]>(() => {
    const all = productsQuery.data ?? [];
    const assigned = new Set(alreadyAssignedProductIds);
    return all
      .filter((p) => !p.deletedAt && !assigned.has(p.id))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [productsQuery.data, alreadyAssignedProductIds]);

  function reset() {
    setProductId(null);
    setJwksUrl('');
    setIssuer('');
    setAudience('');
    setError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleAssign() {
    if (!productId) {
      setError('Pick a product to assign.');
      return;
    }
    setError(null);
    assign.mutate(
      {
        product_id: productId,
        actor_context_jwks_url: jwksUrl.trim() || undefined,
        actor_context_issuer: issuer.trim() || undefined,
        actor_context_audience: audience.trim() || undefined,
      },
      {
        onSuccess: () => handleClose(),
        onError: (err) => setError(parseMutationError(err, 'Could not assign product.')),
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Assign product"
      transitionProps={{ duration: 0 }}
    >
      <Stack>
        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {error}
          </Alert>
        )}
        {candidates.length === 0 ? (
          <Text c="dimmed">All active products are already assigned to this organisation.</Text>
        ) : (
          <>
            <Select
              label="Product"
              placeholder="Pick one"
              required
              value={productId}
              onChange={setProductId}
              data={candidates.map((p) => ({ value: p.id, label: `${p.code} (${p.displayName})` }))}
              searchable
            />
            <TextInput
              label="X-Actor-Context JWKS URL (optional)"
              value={jwksUrl}
              onChange={(e) => setJwksUrl(e.currentTarget.value)}
              placeholder="https://issuer.example.com/.well-known/jwks.json"
            />
            <TextInput
              label="Issuer (optional)"
              value={issuer}
              onChange={(e) => setIssuer(e.currentTarget.value)}
            />
            <TextInput
              label="Audience (optional)"
              value={audience}
              onChange={(e) => setAudience(e.currentTarget.value)}
            />
          </>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose} disabled={assign.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            loading={assign.isPending}
            disabled={candidates.length === 0 || !productId}
          >
            Assign
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
